import { isApiError } from '../../services/apiError'
import { adminPageMayFetch } from '../../utils/adminGate'
import type { AdminGate } from '../../utils/adminGate'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, relativeTime } from '../../utils/format'
import type {
  RegistryOverview,
  RegistryQuery,
  RegistryReport,
  RegistryReportStatus,
  RegistryStatusFilter,
} from '../../types/registry'
import { REGISTRY_STATUSES, REGISTRY_STATUS_FILTER } from '../../types/registry'

/**
 * Pure helpers for the Report Registry page and its Overview preview.
 *
 * Every cell the registry renders from a nullable or typed field is decided
 * here, so the rules a reader relies on — a group owner is nobody, a null
 * workspace is a personal report, a null modification is "never edited" — are
 * pinned by registryFormat.test.ts rather than by whoever next edits a column.
 *
 * Imports only modules that are safe under vitest's node environment
 * (services/apiError, utils/format, types): nothing here may reach MSAL.
 */

/** Fifty, as the server's default page and the app's other paged lists. */
export const REGISTRY_PAGE_SIZE = 50

/** The Overview preview's row count. */
export const REGISTRY_PREVIEW_SIZE = 5

/** The surface name RestrictedState uses on the page and the preview. */
export const REGISTRY_SURFACE = 'Report Registry'

/** What the search box can find. Stated because a miss otherwise reads as "no such report". */
export const SEARCH_SCOPE_CAPTION = 'Matches name and description only'

/* ---- The gate ------------------------------------------------------------ */

/**
 * Whether a registry surface may call the endpoint.
 *
 * The PAGE fetches on `admin` and on `unknown` — a catalogue that failed or
 * predates the permission must never lock an administrator out, and the
 * endpoint's own 403 is then rendered as restricted — but not on `pending`: on a
 * cold load the answer is on its way, and asking first races it. The PREVIEW
 * fetches only on `admin`: a card on every Overview visit must not call an
 * endpoint the caller may not read. Neither fetches on `denied`.
 */
export function registryFetchEnabled(gate: AdminGate, surface: 'page' | 'preview'): boolean {
  return surface === 'page' ? adminPageMayFetch(gate) : gate.state === 'admin'
}

/* ---- Filters and the query ----------------------------------------------- */

/** A click-to-filter value: the id the server matches on, and what the chip says. */
export interface RegistryPick {
  id: string
  label: string
}

/** What the page is filtering on. Search is the applied term, not the draft in the box. */
export interface RegistryFilters {
  status: RegistryStatusFilter | null
  search: string
  owner: RegistryPick | null
  workspace: RegistryPick | null
}

export const NO_REGISTRY_FILTERS: RegistryFilters = {
  status: null,
  search: '',
  owner: null,
  workspace: null,
}

/** Whether anything is narrowing the list — which decides the header and the empty-state copy. */
export function isRegistryFiltered(filters: RegistryFilters): boolean {
  return filters.status !== null
    || filters.search.trim() !== ''
    || filters.owner !== null
    || filters.workspace !== null
}

/**
 * The filters and page as the endpoint's query.
 *
 * Every key is always present, so two equal filter states build equal keys
 * (useRegistry keys on the query by value). registryApi drops the nulls and
 * blanks from the request line.
 */
export function toRegistryQuery(filters: RegistryFilters, page: number): RegistryQuery {
  const search = filters.search.trim()
  return {
    skip: (Math.max(1, page) - 1) * REGISTRY_PAGE_SIZE,
    top: REGISTRY_PAGE_SIZE,
    status: filters.status,
    search: search === '' ? null : search,
    owner: filters.owner?.id ?? null,
    workspaceId: filters.workspace?.id ?? null,
  }
}

/** The status Select's value for "no status filter". */
export const ALL_STATUSES = 'all'

/** The status Select's options: All, then the six in the server's order. */
export function statusFilterOptions(): { value: string; label: string }[] {
  return [
    { value: ALL_STATUSES, label: 'All statuses' },
    ...REGISTRY_STATUSES.map((status) => ({ value: REGISTRY_STATUS_FILTER[status], label: statusLabel(status) })),
  ]
}

/**
 * Mantine's `string | null`, narrowed by checking rather than asserting. "All",
 * null and anything unknown mean no status filter — an unknown value reaching
 * the endpoint would be a 400, not a filter.
 */
export function asStatusFilter(value: string | null): RegistryStatusFilter | null {
  return REGISTRY_STATUSES.map((status) => REGISTRY_STATUS_FILTER[status]).find((f) => f === value) ?? null
}

/* ---- Header and empty states --------------------------------------------- */

/**
 * "12 of 340 reports" when filtered, "340 reports" when not.
 *
 * The denominator is `reportsInRegistry`, taken by the server before any filter,
 * never the page's row count.
 */
export function registryCountText(
  overview: Pick<RegistryOverview, 'totalCount' | 'reportsInRegistry'>,
  filtered: boolean,
): string {
  const noun = overview.reportsInRegistry === 1 ? 'report' : 'reports'
  return filtered
    ? `${exactNumber(overview.totalCount)} of ${exactNumber(overview.reportsInRegistry)} ${noun}`
    : `${exactNumber(overview.reportsInRegistry)} ${noun}`
}

/** The preview's subtitle. */
export function registryPreviewSubtitle(overview: Pick<RegistryOverview, 'reportsInRegistry'>): string {
  const noun = overview.reportsInRegistry === 1 ? 'report' : 'reports'
  return `${exactNumber(overview.reportsInRegistry)} ${noun} in the registry`
}

/**
 * What an empty page says.
 *
 * A filtered miss says only that nothing matched: it never claims anything about
 * the estate ("there are no archived reports"), because a search that looks at
 * two fields and an owner id that matches nobody are not evidence of absence.
 * Only the unfiltered, served denominator of zero may say the registry is empty.
 */
export function registryEmptyText(
  overview: Pick<RegistryOverview, 'totalCount' | 'reportsInRegistry'>,
  filtered: boolean,
): { title: string; message: string } {
  if (overview.totalCount > 0) {
    return {
      title: 'No reports on this page',
      message: `${exactNumber(overview.totalCount)} match, but this page is past the last of them. Go back to page 1.`,
    }
  }
  if (filtered) {
    return {
      title: 'No reports match',
      message: 'Nothing matches these filters. Search reads name and description only, and an owner or '
        + 'workspace filter matches one exact id. Clear the filters to see the whole registry.',
    }
  }
  if (overview.reportsInRegistry === 0) {
    return {
      title: 'No reports in the registry',
      message: 'Cartographer holds no saved report yet. Reports appear here, in every status, once someone saves one.',
    }
  }
  // Unfiltered, a denominator above zero, and nothing on page 1: the two counts disagree.
  return {
    title: 'No reports returned',
    message: `The registry counts ${exactNumber(overview.reportsInRegistry)} but returned none. Refresh to ask again.`,
  }
}

/* ---- Cells ---------------------------------------------------------------- */

/** "Attention required" from "AttentionRequired". */
export function statusLabel(status: RegistryReportStatus): string {
  return status === 'AttentionRequired' ? 'Attention required' : status
}

/**
 * How a status is drawn. The three states a report can still be in are status
 * badges; the three end states are plain gray badges, because "archived" is not
 * a health reading and colouring it would make it one.
 */
export type StatusPresentation =
  | { kind: 'status'; role: 'good' | 'warning' | 'serious'; label: string }
  | { kind: 'plain'; label: string }

export function statusPresentation(status: RegistryReportStatus): StatusPresentation {
  switch (status) {
    case 'Active': return { kind: 'status', role: 'good', label: statusLabel(status) }
    case 'AttentionRequired': return { kind: 'status', role: 'warning', label: statusLabel(status) }
    case 'Delisted': return { kind: 'status', role: 'serious', label: statusLabel(status) }
    default: return { kind: 'plain', label: statusLabel(status) }
  }
}

/** The workspace cell: text, whether it can be clicked to filter, and the personal-workspace badge. */
export interface WorkspaceCell {
  text: string
  /** The workspace kind is Personal. Rare: a personal report is normally not filed at all. */
  personalWorkspace: boolean
  /** The workspace to filter on, or null when the report is filed nowhere (the server cannot filter on "none"). */
  pick: RegistryPick | null
  /** The workspace id on hover, or null. */
  tooltip: string | null
}

/**
 * A null `workspaceId` IS how a personal report is stored — Cartographer files
 * personal reports into no workspace — so it reads "Personal (not filed)",
 * never as shared and never as a dash.
 */
export function workspaceCell(report: RegistryReport): WorkspaceCell {
  if (report.workspaceId == null) {
    return { text: 'Personal (not filed)', personalWorkspace: false, pick: null, tooltip: null }
  }
  const text = report.workspaceName ?? NOT_MEASURED
  return {
    text,
    personalWorkspace: report.isPersonalWorkspace,
    pick: { id: report.workspaceId, label: report.workspaceName ?? report.workspaceId },
    tooltip: `Workspace ${report.workspaceId}`,
  }
}

/** The owner cell: the main line, a dimmed note, the id on hover, and the click-to-filter value. */
export interface OwnerCell {
  text: string
  note: string | null
  tooltip: string | null
  pick: RegistryPick | null
}

/**
 * The owner, read from the SERVED principal type.
 *
 *  - No owner id at all: "None".
 *  - A User: the stamped display name, or NOT_MEASURED with the id on hover. The
 *    server withholds the name after a transfer rather than serve the previous
 *    owner's, and this never substitutes anything for it.
 *  - A Group or Role: named as such, with the fact that matters — Cartographer's
 *    gate matches an owner only as a single user and does not expand a group, so
 *    nobody holds owner rights through it. Never rendered as an unnamed person,
 *    and never given the display name (which describes a user).
 *  - An id with no served type (an API predating the field): the type is not
 *    guessed, because guessing "User" is exactly the error the field fixed.
 */
export function ownerCell(report: RegistryReport): OwnerCell {
  const id = report.ownerUserId
  if (id == null || id.trim() === '') {
    return { text: 'None', note: null, tooltip: null, pick: null }
  }

  switch (report.ownerPrincipalType) {
    case 'User': {
      const name = report.ownerDisplayName?.trim() ? report.ownerDisplayName.trim() : null
      return {
        text: name ?? NOT_MEASURED,
        note: null,
        tooltip: name
          ? `User ${id}`
          : `User ${id}. No name is recorded for this owner: a name is stamped only at creation and is not carried across a transfer.`,
        pick: { id, label: name ?? id },
      }
    }
    case 'Group':
    case 'Role': {
      const kind = report.ownerPrincipalType
      return {
        text: `${kind} ${id}`,
        note: `owns nothing: ${kind.toLowerCase()} membership is not expanded, so nobody holds owner rights through it`,
        tooltip: `${kind} ${id}`,
        pick: { id, label: `${kind} ${id}` },
      }
    }
    default:
      return {
        text: NOT_MEASURED,
        note: 'The API did not say what kind of principal owns this',
        tooltip: `Principal ${id}`,
        pick: { id, label: id },
      }
  }
}

/** The owner cell as one line, as the spec phrases it — also the accessible label. */
export function ownerCellText(report: RegistryReport): string {
  const cell = ownerCell(report)
  return cell.note ? `${cell.text} — ${cell.note}` : cell.text
}

/**
 * The registry's timestamps, read as UTC.
 *
 * They are C# `DateTime`s written as UTC, and SQL Server hands them back with no
 * kind, so the wire value can carry no offset (`2026-09-01T12:00:00`) — which
 * `Date` parses as LOCAL time, hours out west of UTC. A date-time with no offset
 * gets a `Z`; one that has an offset, and a bare date, are left as they are.
 */
export function asUtc(iso: string): string {
  const value = iso.trim()
  if (!value.includes('T')) return value
  return /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`
}

/** A timestamp as the table shows it: the relative form, with the wall clock on hover. */
export interface RegistryWhen {
  text: string
  full: string | null
}

export function registryWhen(iso: string): RegistryWhen {
  const utc = asUtc(iso)
  const parsed = new Date(utc)
  if (Number.isNaN(parsed.getTime())) return { text: NOT_MEASURED, full: null }
  return { text: relativeTime(utc), full: parsed.toLocaleString() }
}

/**
 * Modified. A null is a genuine "never edited", not a withheld value — and it is
 * never replaced by the creation instant, which would make every report look
 * recently touched.
 */
export function modifiedWhen(iso: string | null): RegistryWhen {
  return iso == null ? { text: 'Never edited', full: null } : registryWhen(iso)
}

/** Last recorded run. Null means Cartographer never recorded one (see the page's caption). */
export function lastRunWhen(iso: string | null): RegistryWhen {
  return iso == null ? { text: 'Never', full: null } : registryWhen(iso)
}

/**
 * Explicit grants to people. Zero is "none explicit" — never "nobody can reach it":
 * owners, maintainers and workspace members reach a report without a grant row.
 */
export function grantsText(count: number): string {
  return count === 0 ? 'none explicit' : exactNumber(count)
}

/**
 * The short caption every group-or-role figure carries: stored, never expanded.
 * One spelling, the Governance Queue's, so the two pages cannot word it apart.
 */
export { GROUP_MEMBERS_NOT_RESOLVABLE } from '../governance/governanceFormat'

/**
 * Group or role grants: a measured count, zero included. Kept apart from the
 * people figure, never added to it — these reach nobody.
 */
export function groupOrRoleGrantsText(count: number): string {
  return exactNumber(count)
}

/** What a row whose report cannot run says about the counts beside it. */
export const NOT_RUNNABLE_NOTE = 'not runnable: counts are view-only'

/**
 * The served runnable verdict, as the note the status cell carries — or null for
 * a report that runs. `isRunnable` is the server's (the shared IsRunnable rule);
 * the page derives nothing from the status itself. An API predating the field
 * (undefined) says nothing rather than a guess.
 */
export function runnableNote(report: Pick<RegistryReport, 'isRunnable'>): string | null {
  return report.isRunnable === false ? NOT_RUNNABLE_NOTE : null
}

/** Active schedules: a measured count, zero included. */
export function schedulesText(count: number): string {
  return exactNumber(count)
}

/* ---- Errors --------------------------------------------------------------- */

/**
 * What a failed registry read prints.
 *
 * A 400 is the server refusing a filter it cannot honour (an unknown status, a
 * workspace that no longer exists), and its ProblemDetails `detail` says which,
 * so it is printed first. Anything else prints the error's first line
 * (errorSummary), never the whole body.
 */
export function registryErrorText(error: unknown): string {
  if (errorKind(error) === 'bad-request' && isApiError(error) && error.detail?.trim()) {
    return error.detail.trim()
  }
  return errorSummary(error) ?? 'The request failed.'
}
